import type { RefObject } from 'react'
import type { AggregatedOrderView } from '@/types/api/aggregated/order'

import { TRADING_EVENTS_HWM_KEY } from '@/components/trade-desk/utils/use-connect-to-websockets'
import { createLogger } from '@/lib/logger'
import {
  type AllocationUpdateEventMessage,
  type OrderUpdateEventMessage,
  type SystemUpdateEventMessage,
  type TradingEventMessage,
  TradingEventMessageType,
} from '@/types/api/events/trading-event'
import { getMarketDayStart } from '@/utils/dates/dateHelpers'
import { isControlMessage } from '@/utils/trade-blotter/control-message'
import { isEventOutsideRange } from '@/utils/trade-blotter/helpers'
import { updateHwmInLocalStorage } from '@/utils/trade-blotter/web-socket'

import {
  handleAllocationUpdate,
  processAllocationUpdate,
} from './allocation-update'
import { handleOrderUpdate, processOrderUpdate } from './order-update'
import { handleSystemUpdate, processSystemUpdate } from './system-update'

const LOGGER = createLogger('utils/api/trading-event/helpers')

/**
 * Upserts an order into the map.
 * If the order already exists, it is updated using the key in constant time.
 * If the order does not exist, it is added to the map in constant time.
 * There is no guarantee of preserving the chronology of the the orders in the map.
 * This is okay because the trade blotter will display the orders
 * in descending order of created_at time using AG Grid.
 * @param map Map of order ID to AggregatedOrderView objects
 * @param orderId ID of the order to upsert
 * @param updatedFields Updated fields for the order
 * @param onUpdateApplied Function to apply the update to the map
 */
export const upsertOrder = (
  map: Map<string, AggregatedOrderView>,
  orderId: string,
  updatedFields: Partial<AggregatedOrderView>,
  onUpdateApplied: ({
    toAdd,
    toUpdate,
    toRemove,
  }: {
    toAdd?: AggregatedOrderView[]
    toUpdate?: AggregatedOrderView[]
    toRemove?: AggregatedOrderView[]
  }) => void,
) => {
  if (!map.has(orderId)) {
    const newOrderView = {
      id: orderId,
      ...updatedFields,
    } as AggregatedOrderView
    map.set(orderId, newOrderView)
    onUpdateApplied({
      toAdd: [newOrderView],
    })
  } else {
    const updatedOrderView = {
      ...map.get(orderId),
      ...updatedFields,
    } as AggregatedOrderView
    map.set(orderId, updatedOrderView)
    onUpdateApplied({
      toUpdate: [updatedOrderView],
    })
  }
}

export const updateLatestMsgNumber = (
  aggregatedOrderView: AggregatedOrderView,
  msgNumber: string,
): AggregatedOrderView => {
  const a = BigInt(aggregatedOrderView.latest_msg_number || 0)
  const b = BigInt(msgNumber)
  const latestMessageNumber = a > b ? a : b
  return {
    ...aggregatedOrderView,
    latest_msg_number: latestMessageNumber.toString(),
  } as AggregatedOrderView
}

/**
 * Filters and processes trading events received via HTTP and returns an array of
 * AggregatedOrderView objects sorted in ascending order by msg_number.
 * Only called once by the TradingSlice after the network call to getTradingEvents completes.
 * @param tradingEventsFromApi Array of TradingEventMessage objects received from the API
 * @param todayStart Start of today in milliseconds
 * @returns Array of AggregatedOrderView objects sorted in ascending order by msg_number
 */
export const filterAndProcessTradingEvents: (
  tradingEventsFromApi: TradingEventMessage[],
  startTime: number,
  endTime?: number,
) => AggregatedOrderView[] = (tradingEventsFromApi, startTime, endTime) => {
  const aggregatedOrderViewsMap: Map<string, AggregatedOrderView> = new Map()
  const distinctOrderIds = new Set<string>()

  tradingEventsFromApi
    .filter((tradingEvent) => {
      return !isEventOutsideRange(tradingEvent?.event?.time, startTime, endTime)
    })
    .forEach((tradingEvent) => {
      if (tradingEvent.msg_type === TradingEventMessageType.AllocationUpdate) {
        processAllocationUpdate(aggregatedOrderViewsMap, tradingEvent)
        distinctOrderIds.add(
          (tradingEvent as AllocationUpdateEventMessage).summary.order_id,
        )
      }
      if (tradingEvent.msg_type === TradingEventMessageType.OrderUpdate) {
        processOrderUpdate(aggregatedOrderViewsMap, tradingEvent)
        distinctOrderIds.add((tradingEvent as OrderUpdateEventMessage).order.id)
      }
      if (tradingEvent.msg_type === TradingEventMessageType.SystemUpdate) {
        processSystemUpdate(aggregatedOrderViewsMap, tradingEvent)
        distinctOrderIds.add(
          (tradingEvent as SystemUpdateEventMessage).event.order_id,
        )
      }
    })
  const aggregatedOrderViews = Array.from(aggregatedOrderViewsMap.values())
  if (distinctOrderIds.size !== aggregatedOrderViews.length) {
    LOGGER.error('Number of distinct orders does not match', {
      error: new Error('Number of distinct orders does not match'),
      distinctOrderIdsSize: distinctOrderIds.size,
      aggregatedOrderViewsSize: aggregatedOrderViews.length,
    })
  }
  // Return the orders in ascending order by msg_number
  // We do this here because we will later call calculateInitialHwm, which has this precondition.
  return aggregatedOrderViews.sort((a, b) => {
    const aMsgNumber = BigInt(a.latest_msg_number || 0)
    const bMsgNumber = BigInt(b.latest_msg_number || 0)
    return Number(aMsgNumber - bMsgNumber)
  })
}

/**
 * Handles a single order-stream websocket message: ignores control frames
 * (heartbeat / connection ack), then applies the order/allocation/system update
 * to the aggregated-view ref and advances the stored HWM.
 */
export function handleWebSocketMessage(
  event: MessageEvent,
  aggregatedOrderViewsRef: RefObject<Map<string, AggregatedOrderView>>,
  onUpdateApplied: ({
    toAdd,
    toUpdate,
    toRemove,
  }: {
    toAdd?: AggregatedOrderView[]
    toUpdate?: AggregatedOrderView[]
    toRemove?: AggregatedOrderView[]
  }) => void,
) {
  // Process each event in the order they are received, which
  // is also in ascending order by msg_number
  const tradingEvent: TradingEventMessage = JSON.parse(event.data)
  // Control frames (heartbeat / connection ack) carry no event payload — ignore
  // them before reading event/HWM fields.
  if (isControlMessage(tradingEvent)) {
    return
  }
  const todayStart = getMarketDayStart()
  const isOutsideRange = isEventOutsideRange(
    tradingEvent?.event?.time,
    todayStart,
  )

  if (
    tradingEvent.msg_type === TradingEventMessageType.OrderUpdate &&
    !isOutsideRange
  ) {
    handleOrderUpdate(tradingEvent, aggregatedOrderViewsRef, onUpdateApplied)
  }

  if (
    tradingEvent.msg_type === TradingEventMessageType.AllocationUpdate &&
    !isOutsideRange
  ) {
    handleAllocationUpdate(
      tradingEvent,
      aggregatedOrderViewsRef,
      onUpdateApplied,
    )
  }

  if (tradingEvent.msg_type === TradingEventMessageType.SystemUpdate) {
    handleSystemUpdate(tradingEvent, aggregatedOrderViewsRef, onUpdateApplied)
  }

  updateHwmInLocalStorage(tradingEvent.msg_number, TRADING_EVENTS_HWM_KEY)
}
